import { equipCollection } from '@/core/equip'
import type { DailyChallengeInfoRes } from '@/types/dungeon'
import type { SpecialKey } from '@/types/rune'
import type { UserAttrType, UserInfoType } from '@/types/user'
import { socketEmit } from '@/utils/web-socket'
import { cloneDeep } from 'lodash'
import { defineStore } from 'pinia'
import type { RelicInfoType, RelicItemType } from '@/types/relic'
import { relicCollection } from '@/core/relic'
import { getSkillLevelByExp } from '@/core/skill/exp'
import { skillCollection } from '@/core/skill'

export const useUserStore = defineStore('user', {
  state: (): UserInfoType => {
    return {
      id: 0,
      username: '',
      email: '',
      nickname: '',
      nicknameColor: '',
      gold: 0,
      diamond: 0,
      crystal: 0,
      duelCrystal: 0,
      vipLevel: 0,
      itemList: [],
      warehouseItemList: [],
      consumables: {
        reinforceProtect: 0,
        itemCapacity: 0,
        riseStarStone: 0,
        antiquityStone: 0,
        warehouseCapacity: 0,
        equippedRoutine: 0,
        petCapacity: 0,
        fusionStabilizer: 0,
        runeBlessCoupon: 0,
        runeCapacity: 0,
        runeStar: 0,
        crackStone: 0,
        crackFight: 0,
        enchantEnhance: 0,
        antiquitySoul: 0,
        forgeStone: 0,
        potentialityStone: 0,
        wildSupply: 0,
        refineStone: 0,
        engraveCoupon: 0,
        mysteryAromatherapy: 0,
        mysteryStone: 0,
        monthlyCard: 0,
        relicStar: 0,
        relicTreasureMap: 0,
        shineBrightlyStone: 0,
        oneYearGift: 0,
        secretCaveFight: 0,
        oneYearPoint: 0,
        purifyStone: 0,
        mooncakeMaterial: 0,
        duelCoin: 0,
        duelDiamond: 0,
        supremeRefineStone: 0,
        supremeEngraveCoupon: 0,
        skillUpgradeStone: 0,
        newYearGift2026: 0
      },
      equippedList: {
        weapon: '',
        armor: '',
        shoes: '',
        helmet: '',
        jewelry: '',
      },
      equippedRoutineId: 1,
      equippedRoutineList: [],
      userAttr: {
        atk: 0,
        atksp: 0,
        crt: 0,
        crtd: 0,
        heat: 0,
        hr: 0,
        voidDef: 0,
        ad: 0,
        windDa: 0,
        fireDa: 0,
        waterDa: 0,
        soilDa: 0,
        windIndDa: 0,
        fireIndDa: 0,
        waterIndDa: 0,
        soilIndDa: 0,
        mp: 0,
        mpRegen: 0,
        cda: 0,
        sce: 0,
        skillDa: 0,
        undeadDa: 0,
        orcDa: 0,
        caveDa: 0,
        elDa: 0,
        elfDa: 0,
        dmDa: 0,
        faeDa: 0,
        mzDa: 0,
        igFireDef: 0,
        igWaterDef: 0,
        igWindDef: 0,
        igSoilDef: 0,
        allSkillGrow: 0,
        fireSkillGrow: 0,
        waterSkillGrow: 0,
        windSkillGrow: 0,
        soilSkillGrow: 0,
        skillGrow: {},
      },
      dailyChallengeLevel: { equip: 0, gold: 0, diamond: 0 },
      dailyChallengeCount: { equip: 0, gold: 0, diamond: 0 },
      dailyChallengeTimes: { equip: 0, gold: 0, diamond: 0 },
      eternalTowerResetTime: '',
      eternalTowerTimes: 0,
      eternalTowerLevel: 0,
      eternalTowerNoviceProtect: 0,
      runeTowerResetTime: '',
      runeTowerTimes: 0,
      runeTowerLevel: 0,
      runeTempleResetTime: '',
      runeTempleTimes: 1,
      crackResetTime: '',
      crackTimes: 0,
      noviceCheckInIndex: 0,
      nestResetTime: '',
      nestTimes: 0,
      runeRuinResetTime: '',
      runeRuinTimes: 0,
      petList: [],
      fightPetId: '',
      outsidePetIdList: ['', ''],
      outsidePetResetTime: '',
      soulType: 0,
      runeList: [],
      runeBlessResetTime: '',
      runeBlessTimes: {
        gold: 0,
        diamond: 0,
      },
      runeEquippedList: [],
      commerceList: [
        { id: 1, count: 0, avgPrice: 0 },
        { id: 2, count: 0, avgPrice: 0 },
        { id: 3, count: 0, avgPrice: 0 },
        { id: 4, count: 0, avgPrice: 0 },
        { id: 5, count: 0, avgPrice: 0 },
      ],
      vendorResetTime: '',
      privacyConfig: {
        showCreated: true,
        showId: false,
        showEquip: false,
        showVip: true,
      },
      guideFightIndex: 0,
      advanceResetTime: '',
      advanceTimes: 0,
      advanceLevel: 0,
      autoEndTime: '',
      autoChangeRoutineList: {},
      advanceAutoChangeRoutineList: {},
      advanceAutoChangeRoutineSet: {
        onlyNoDungeon: true,
        onlyOffline: true,
      },
      endlessTowerResetTime: '',
      endlessTowerTimes: 0,
      endlessTowerLevel: 0,
      advanceAutoEndTime: '',
      goldTempleResetTime: '',
      goldTempleTimes: 0,
      goldTempleRewardTimes: 0,
      relicList: [],
      relicBlessTimes: 0,
      relicEquippedList: [],
      relicRuinResetTime: '',
      relicRuinTimes: 0,
      rechargeLevel: 0,
      drageonMountainResetTime: '',
      drageonMountainTimes: 0,
      drageonMountainRewardTimes: 0,
      peakResetTime: '',
      peakTimes: 0,
      peakLevel: 0,
      skillList: [],
      skillEquippedList: [],
      skillExpList: {},
      peakAutoEndTime: '',
      peakAutoChangeRoutineList: {},
      boundlessTowerResetTime: '',
      boundlessTowerTimes: 0,
      boundlessTowerLevel: 0,
      token: '',
      created: '',
    }
  },
  getters: {
    // 获取装备中的装备id
    getEquippedEquipIdList(state) {
      const itemIdList = Object.values(state.equippedList)
      return itemIdList.map((itemId) => {
        return state.itemList.find((item) => item.id === itemId)?.equipId
      })
    },
    // 获取背包中的装备数量
    getItemEquipTotal(state) {
      const itemIdList = Object.values(state.equippedList)
      const equippedNum = itemIdList.filter((item) => item).length
      return state.itemList.length - equippedNum
    },
    // 获取背包中的装备
    getItemEquipInfoList(state) {
      const items = state.itemList
      if (state.itemList.length === 0) return []
      return items
        .filter((item) => {
          const equippedIdList = Object.values(state.equippedList)
          return !equippedIdList.includes(item.id)
        })
        .map((item) => {
          const { equipId } = item
          const { name, type, attrs } = equipCollection[equipId]
          return { ...item, name, type, attrs }
        })
    },
    // 获取仓库中的装备
    getWarehouseItemEquipInfoList(state) {
      const items = state.warehouseItemList
      if (!items) return []
      if (items.length === 0) return []
      return items.map((item) => {
        const { equipId } = item
        const { name, type, attrs } = equipCollection[equipId]
        return { ...item, name, type, attrs }
      })
    },
    getItemCapacity(state) {
      return 50 + state.consumables.itemCapacity * 10
    },
    getWarehouseCapacity(state) {
      return 200 + state.consumables.warehouseCapacity * 10
    },
    getPetCapacity(state) {
      return 30 + state.consumables.petCapacity * 10 + 3
    },
    checkPetCapacity(state) {
      return state.petList.length > 30 + state.consumables.petCapacity * 10
    },
    getRuneCapacity(state) {
      return 100 + state.consumables.runeCapacity * 10
    },
    checkRuneCapacity(state) {
      return state.runeList.length > 100 + state.consumables.runeCapacity * 10
    },
    getExtraAttrs(state) {
      const res: Partial<Record<SpecialKey, any>> = {}
      const runeEquippedItemList = state.runeList.filter((item) => state.runeEquippedList.includes(item.id))
      runeEquippedItemList.forEach((item) => {
        const specAttr = item.attrs.special
        specAttr.forEach((item) => {
          for (const key in item.data) {
            if (!res[item.key]) {
              res[item.key] = {}
            }
            if (!res[item.key][key]) {
              res[item.key][key] = 0
            }
            res[item.key][key] += item.data[key]
          }
        })
      })
      return res
    },
    // 获取背包圣物信息列表
    getItemRelicInfoList(state): RelicInfoType[] {
      return state.relicList
        .filter((item) => !state.relicEquippedList.includes(item.id))
        .map((item) => {
          const relicInfo = relicCollection[item.relicId]
          return {
            ...relicInfo,
            ...item,
          }
        })
    },
  },
  actions: {
    async setDailyChallengeInfo() {
      socketEmit<DailyChallengeInfoRes>({
        type: 'getDailyChallengeInfo',
        callBack: (res) => {
          const data = res.data
          this.dailyChallengeTimes = data.times
          this.dailyChallengeLevel = data.level
        },
      })
    },
    setRoutineEquippedList() {
      const routine = this.equippedRoutineList.find((item) => item.id === this.equippedRoutineId)
      if (!routine) return
      routine.equippedList = { ...this.equippedList }
      routine.runeEquippedList = [...this.runeEquippedList]
      routine.relicEquippedList = [...this.relicEquippedList]
    },
    handleChangeEquipRoutine(userAttr: UserAttrType, routineId: number) {
      const routine = cloneDeep(this.equippedRoutineList.find((item) => item.id === routineId))
      if (!routine) return
      Object.entries(routine.equippedList).forEach(([key, value]) => {
        const itemInfo = this.itemList.find((item) => item.id === value)
        // @ts-ignore
        if (!itemInfo) routine.equippedList[key] = ''
      })
      this.equippedList = routine.equippedList
      this.equippedRoutineId = routineId
      const fightPetId = routine.fightPetId
      if (fightPetId && !this.outsidePetIdList.includes(fightPetId)) {
        if (this.petList.find((item) => item.id === fightPetId)) {
          this.fightPetId = fightPetId
        }
      }
      if (routine.runeEquippedList) {
        this.runeEquippedList = routine.runeEquippedList
      } else {
        this.runeEquippedList = ['', '', '', '', '']
      }
      if (routine.relicEquippedList) {
        this.relicEquippedList = routine.relicEquippedList
      } else {
        this.relicEquippedList = ['', '', '', '', '']
      }
      if (routine.skillEquippedList) {
        this.skillEquippedList = routine.skillEquippedList
      } else {
        this.skillEquippedList = ['', '', '', '']
      }
      this.userAttr = userAttr
    },
    getRelicToBag(relicList: RelicItemType[]) {
      relicList.forEach((item) => {
        const relicInfo = this.relicList.find((relic) => item.relicId === relic.relicId)
        if (relicInfo) {
          relicInfo.count++
        } else {
          this.relicList.push(item)
        }
      })
    },
    getSkillExpLevel(skillId: number) {
      const exp = this.skillExpList[skillId] || 0
      return getSkillLevelByExp(exp)
    },
    getSkillRealLevel(skillId: number) {
      const level = this.getSkillExpLevel(skillId)

      let realLevel = level
      const element = skillCollection[skillId].element[0]
      const { skillGrow, fireSkillGrow, windSkillGrow, waterSkillGrow, soilSkillGrow, allSkillGrow } = this.userAttr
      const skillGrowLevel = skillGrow[skillId] || 0
      realLevel += skillGrowLevel + allSkillGrow
      switch (element) {
        case 'fire':
          realLevel += fireSkillGrow
          break
        case 'wind':
          realLevel += windSkillGrow
          break
        case 'water':
          realLevel += waterSkillGrow
          break
        case 'soil':
          realLevel += soilSkillGrow
          break
      }

      return realLevel
    },
    getSkillLevelAddtion(skillId: number, k = 0.7) {
      let level = this.getSkillRealLevel(skillId)
      if (level < 0) level = 0

      return 1 + 2 * Math.pow(level / 50, k)
    },
    getSkillAtkAddtion(k = 1) {
      let atk = this.userAttr.atk
      atk -= 100
      return 1 + (k * atk) / (atk + 500)
    },
  },
})
